import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Trash2,
  FileText,
  HelpCircle,
  Globe,
  Type,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Search,
  File,
  X,
  Check,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { DocumentExtractionPanel } from "./DocumentExtractionPanel";

type DocFormType = "text" | "faq" | "url";

const emptyForm = {
  title: "",
  sourceType: "text" as DocFormType,
  content: "",
  sourceUrl: "",
};

const SOURCE_TYPE_LABELS: Record<string, { label: string; icon: typeof FileText; description: string }> = {
  text: { label: "Texto livre", icon: Type, description: "Cole conteúdo sobre sua empresa, produtos ou serviços" },
  faq: { label: "FAQ (Perguntas e Respostas)", icon: HelpCircle, description: "Formato pergunta/resposta. Cada bloco vira um chunk separado" },
  url: { label: "URL (página web)", icon: Globe, description: "Cole o conteúdo extraído de uma página web" },
  pdf: { label: "PDF importado", icon: FileText, description: "" },
  docx: { label: "Documento (DOCX)", icon: File, description: "" },
};

const STATUS_BADGES: Record<string, { label: string; variant: "default" | "secondary" | "destructive"; icon: typeof Loader2 }> = {
  processing: { label: "Processando", variant: "secondary", icon: Loader2 },
  ready: { label: "Pronto", variant: "default", icon: CheckCircle2 },
  error: { label: "Erro", variant: "destructive", icon: AlertCircle },
};

const URL_REANALYZE_DAYS = 7;

function evaluateLock(doc: {
  sourceType: string;
  analyzedAt?: Date | string | null;
  analyzedData?: Record<string, any> | null;
}): { locked: boolean; reason: string | null; analyzedAt: string | null } {
  const at = doc.analyzedAt;
  const data = doc.analyzedData;
  if (!at || !data) return { locked: false, reason: null, analyzedAt: null };
  const errors = (data as { errors?: Record<string, string> }).errors;
  const hasErrors = !!errors && Object.keys(errors).length > 0;
  const analyzedAtIso = new Date(at).toISOString();
  if (hasErrors) {
    return { locked: false, reason: "Análise anterior teve erros — você pode reanalisar", analyzedAt: analyzedAtIso };
  }
  if (doc.sourceType === "url") {
    const days = (Date.now() - new Date(at).getTime()) / (1000 * 60 * 60 * 24);
    if (days >= URL_REANALYZE_DAYS) {
      return { locked: false, reason: null, analyzedAt: analyzedAtIso };
    }
    const remaining = Math.max(1, Math.ceil(URL_REANALYZE_DAYS - days));
    return {
      locked: true,
      reason: `URL já analisada — reanálise liberada em ${remaining} dia${remaining > 1 ? "s" : ""}`,
      analyzedAt: analyzedAtIso,
    };
  }
  return {
    locked: true,
    reason: "Documento já analisado — análise por arquivo é única",
    analyzedAt: analyzedAtIso,
  };
}

export function DocumentsPanel() {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<string[] | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [analyzingId, setAnalyzingId] = useState<number | null>(null);
  const utils = trpc.useUtils();

  const { data: documents, isLoading } = trpc.knowledgeBase.list.useQuery();

  const createMut = trpc.knowledgeBase.create.useMutation({
    onSuccess: () => {
      utils.knowledgeBase.list.invalidate();
      toast.success("Documento adicionado! Processando chunks...");
      setCreating(false);
      setForm(emptyForm);
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const deleteMut = trpc.knowledgeBase.delete.useMutation({
    onSuccess: () => {
      utils.knowledgeBase.list.invalidate();
      toast.success("Documento removido");
      setConfirmDeleteId(null);
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const searchMut = trpc.knowledgeBase.search.useMutation({
    onSuccess: (data) => {
      setSearchResults(data);
      if (data.length === 0) toast.info("Nenhum resultado encontrado");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const handleSubmit = () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Título e conteúdo são obrigatórios");
      return;
    }
    createMut.mutate({
      title: form.title,
      sourceType: form.sourceType,
      content: form.content,
      sourceUrl: form.sourceUrl || null,
    });
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    searchMut.mutate({ query: searchQuery });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Documentos & FAQs</h2>
          <p className="text-sm text-muted-foreground">
            Treine a IA com conteúdo da empresa: políticas, FAQs, processos. Para subir PDFs ou capturar de uma URL, use a aba <strong>Importar</strong>.
          </p>
        </div>
        {!creating && (
          <Button onClick={() => { setForm(emptyForm); setCreating(true); }}>
            <Plus className="w-4 h-4 mr-1" />
            Novo documento
          </Button>
        )}
      </div>

      {creating && (
        <Card className="p-5 border-primary/40 bg-primary/[0.02]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm">Novo documento</h3>
            <Button variant="ghost" size="icon" onClick={() => { setCreating(false); setForm(emptyForm); }} className="h-7 w-7">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="space-y-4">
            <div>
              <Label>Título</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Ex: Política de trocas e devoluções"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Tipo de fonte</Label>
              <Select value={form.sourceType} onValueChange={(v: DocFormType) => setForm({ ...form, sourceType: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(["text", "faq", "url"] as const).map((key) => (
                    <SelectItem key={key} value={key}>
                      {SOURCE_TYPE_LABELS[key].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">{SOURCE_TYPE_LABELS[form.sourceType].description}</p>
            </div>
            {form.sourceType === "url" && (
              <div>
                <Label>URL de origem (opcional)</Label>
                <Input
                  value={form.sourceUrl}
                  onChange={(e) => setForm({ ...form, sourceUrl: e.target.value })}
                  placeholder="https://exemplo.com/politica-trocas"
                  className="mt-1"
                />
              </div>
            )}
            <div>
              <Label>Conteúdo</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder={form.sourceType === "faq"
                  ? "P: Qual o prazo de entrega?\nR: O prazo de entrega é de 3 a 7 dias úteis.\n\nP: Como faço para trocar?\nR: Acesse nosso site na seção Trocas..."
                  : "Cole aqui o texto sobre sua empresa, produtos, serviços, políticas..."}
                className="mt-1 min-h-[200px]"
                rows={10}
              />
              <p className="text-[11px] text-muted-foreground mt-1">{form.content.length} caracteres</p>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => { setCreating(false); setForm(emptyForm); }}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit} disabled={createMut.isPending}>
                {createMut.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                Adicionar
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Testar busca na base de conhecimento..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={handleSearch} disabled={searchMut.isPending}>
              {searchMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buscar"}
            </Button>
          </div>
          {searchResults && searchResults.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">{searchResults.length} chunks encontrados:</p>
              {searchResults.map((chunk, i) => (
                <div key={i} className="text-xs bg-muted/50 rounded p-2 border">
                  {chunk.slice(0, 300)}
                  {chunk.length > 300 ? "..." : ""}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : !documents || documents.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Nenhum documento adicionado ainda</p>
            <p className="text-xs text-muted-foreground/60 mt-1">
              Adicione textos sobre seus produtos, serviços, políticas e FAQs
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {documents.map((doc) => {
            const statusInfo = STATUS_BADGES[doc.status] || STATUS_BADGES.processing;
            const sourceInfo = SOURCE_TYPE_LABELS[doc.sourceType] || SOURCE_TYPE_LABELS.text;
            const SourceIcon = sourceInfo.icon;
            const StatusIcon = statusInfo.icon;
            const isConfirming = confirmDeleteId === doc.id;
            const isExpanded = expandedId === doc.id;
            const isAnalyzing = analyzingId === doc.id;
            const isReady = doc.status === "ready";
            const content = (doc as { originalContent?: string | null }).originalContent ?? "";
            const docWithAnalysis = doc as typeof doc & {
              analyzedAt?: Date | string | null;
              analyzedData?: Record<string, any> | null;
            };
            const lock = evaluateLock(docWithAnalysis);
            const hasSavedAnalysis = !!docWithAnalysis.analyzedAt && !!docWithAnalysis.analyzedData;
            const analyzeLabel = isAnalyzing
              ? "Fechar análise"
              : hasSavedAnalysis
                ? "Ver análise"
                : "Analisar com IA";

            return (
              <Card key={doc.id} className="group">
                <CardContent className="py-3 px-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="p-2 bg-indigo-50 rounded-lg shrink-0">
                        <SourceIcon className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-semibold truncate">{doc.title}</h3>
                          <Badge variant={statusInfo.variant} className="text-[10px] px-1.5 py-0 h-5 shrink-0">
                            <StatusIcon className={`w-3 h-3 mr-1 ${doc.status === "processing" ? "animate-spin" : ""}`} />
                            {statusInfo.label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                          <span>{sourceInfo.label}</span>
                          <span>{doc.chunkCount} chunks</span>
                          <span>{new Date(doc.createdAt).toLocaleDateString("pt-BR")}</span>
                          {content && <span>{content.length.toLocaleString("pt-BR")} caracteres</span>}
                          {hasSavedAnalysis && lock.analyzedAt && (
                            <span className="text-emerald-700">
                              ✓ Analisado em {new Date(lock.analyzedAt).toLocaleDateString("pt-BR")}
                            </span>
                          )}
                        </div>
                        {doc.errorMessage && <p className="text-xs text-destructive mt-1">{doc.errorMessage}</p>}
                      </div>
                    </div>
                    {!isConfirming ? (
                      <div className="flex gap-1 shrink-0">
                        {isReady && content && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => setExpandedId(isExpanded ? null : doc.id)}
                          >
                            {isExpanded ? (
                              <>
                                <ChevronUp className="w-3.5 h-3.5 mr-1" /> Ocultar
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-3.5 h-3.5 mr-1" /> Conteúdo
                              </>
                            )}
                          </Button>
                        )}
                        {isReady && content && (
                          <Button
                            variant={isAnalyzing ? "default" : "outline"}
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => setAnalyzingId(isAnalyzing ? null : doc.id)}
                          >
                            {analyzeLabel}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 h-7 w-7"
                          onClick={() => setConfirmDeleteId(doc.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-1 items-center shrink-0">
                        <span className="text-[10px] text-muted-foreground mr-1">Remover?</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setConfirmDeleteId(null)}
                          disabled={deleteMut.isPending}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive"
                          onClick={() => deleteMut.mutate({ id: doc.id })}
                          disabled={deleteMut.isPending}
                        >
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {isExpanded && content && (
                    <div className="border-t pt-3 mt-3">
                      <pre className="text-[11px] text-muted-foreground whitespace-pre-wrap font-sans bg-muted/30 rounded p-3 max-h-[400px] overflow-y-auto">
                        {content}
                      </pre>
                    </div>
                  )}

                  {isAnalyzing && content && (
                    <DocumentExtractionPanel
                      documentId={doc.id}
                      autoStart={!hasSavedAnalysis && !lock.locked}
                      savedAnalysis={hasSavedAnalysis ? (docWithAnalysis.analyzedData as any) : null}
                      analyzedAt={lock.analyzedAt}
                      canReanalyze={!lock.locked}
                      lockReason={lock.reason}
                    />
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
